import React, { useState } from 'react';
import { Box, Typography, keyframes, Tooltip } from '@mui/material';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SitesMapModal from './SitesMapModal';

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 1 }
  50%      { transform: scale(1.4); opacity: 0.55 }
`;

/**
 * Decorative circuit/orbital artwork — same visual language as the Launchpad
 * banner (LaunchStatusBanner) so the two hero panels feel like a set.
 */
const CircuitArtwork = () => {
  const stroke = 'rgba(125, 211, 252, 0.12)';
  const fill = 'rgba(125, 211, 252, 0.10)';
  return (
    <Box
      component="svg"
      viewBox="0 0 360 200"
      preserveAspectRatio="xMidYMid slice"
      sx={{ position: 'absolute', right: -20, top: -20, bottom: -20, width: 360, pointerEvents: 'none', zIndex: 0 }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="dbb-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="260" cy="100" r="80" fill="none" stroke={stroke} strokeWidth="1" strokeDasharray="2 6" />
      <circle cx="260" cy="100" r="56" fill="none" stroke={stroke} strokeWidth="1" />
      <circle cx="260" cy="100" r="32" fill="url(#dbb-core)" />
      {[
        [180, 100], [340, 100], [260, 20], [260, 180],
        [205, 45], [315, 155], [205, 155], [315, 45],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="3" fill={fill} />
      ))}
      {[
        [180, 100, 260, 100], [260, 100, 340, 100],
        [260, 100, 260, 20], [260, 100, 260, 180],
        [205, 45, 260, 100], [315, 155, 260, 100],
      ].map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth="1" />
      ))}
    </Box>
  );
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

/**
 * Dashboard hero banner — a federated-network overview that anchors the page.
 * Mirrors the Launchpad banner's navy/circuit styling but summarises the live
 * federation: connected client sites, datasets, and models. All values come
 * from props the Dashboard already derives from AppContext (no new calls).
 */
const DashboardBanner = ({
  userName,
  sitesOnline = 0,
  sitesTotal = 0,
  datasetCount = 0,
  modelCount = 0,
  sites = [],
  capabilitiesBySite = {},
}) => {
  const accent = '#7dd3fc';
  const allOnline = sitesTotal > 0 && sitesOnline === sitesTotal;
  const statusColor = sitesTotal === 0 ? accent : allOnline ? '#86efac' : '#fbbf24';
  const name = userName ? `, ${userName}` : '';
  const [mapOpen, setMapOpen] = useState(false);

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        background: 'linear-gradient(135deg, #1B365D 0%, #132643 60%, #0b1629 100%)',
        color: '#ffffff',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        boxShadow: '0 6px 18px rgba(15, 23, 42, 0.18)',
        mb: 3,
        minHeight: 120,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <CircuitArtwork />

      <Box sx={{ position: 'relative', zIndex: 1, p: 2.5, display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
        {/* Icon tile */}
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 1.5,
            backgroundColor: 'rgba(255, 255, 255, 0.10)',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accent,
            flexShrink: 0,
            backdropFilter: 'blur(6px)',
          }}
        >
          <HubOutlinedIcon sx={{ fontSize: 26 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: accent,
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              mb: 0.25,
            }}
          >
            Federated Learning Network
          </Typography>
          <Typography sx={{ color: '#ffffff', fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
            {greeting()}{name}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.8125rem', mt: 0.5, lineHeight: 1.5, maxWidth: 660 }}>
            Your federation spans{' '}
            <Box component="span" sx={{ color: '#ffffff', fontWeight: 600 }}>{sitesTotal} client {sitesTotal === 1 ? 'site' : 'sites'}</Box>
            {' '}with{' '}
            <Box component="span" sx={{ color: '#ffffff', fontWeight: 600 }}>{datasetCount} {datasetCount === 1 ? 'dataset' : 'datasets'}</Box>
            {' '}and{' '}
            <Box component="span" sx={{ color: '#ffffff', fontWeight: 600 }}>{modelCount} {modelCount === 1 ? 'model' : 'models'}</Box>
            {' '}ready for training. Monitor federation health and launch runs from here.
          </Typography>
        </Box>

        {/* Right-side live status pill — opens the federation map */}
        <Tooltip title="View sites on map" placement="top" arrow>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setMapOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setMapOpen(true);
              }
            }}
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: statusColor,
              fontSize: '0.7rem',
              fontWeight: 600,
              fontFamily: 'var(--fx-font-mono)',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '0.02em',
              alignSelf: 'flex-end',
              flexShrink: 0,
              cursor: 'pointer',
              transition: 'background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.16)',
                borderColor: 'rgba(255,255,255,0.3)',
                transform: 'translateY(-1px)',
              },
              '&:focus-visible': {
                outline: `2px solid ${accent}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: statusColor,
                boxShadow: `0 0 6px ${statusColor}`,
                animation: `${pulseDot} 1.8s ease-in-out infinite`,
              }}
            />
            {sitesOnline} of {sitesTotal} sites online
          </Box>
        </Tooltip>
      </Box>

      <SitesMapModal
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        sites={sites}
        capabilitiesBySite={capabilitiesBySite}
      />
    </Box>
  );
};

export default DashboardBanner;
